// src/lib/convertHeic.ts
// Using libheif-js v1.17+ style API
type HeifImage = {
  get_width(): number;
  get_height(): number;
  display(
    imageData: ImageData,
    cb: (
      displayData: {
        width: number;
        height: number;
        data: Uint8ClampedArray;
      } | null,
    ) => void,
  ): void;
};

type LibHeif = {
  HeifDecoder: new () => {
    decode(data: Uint8Array): HeifImage[];
  };
};

// Lazy-load libheif; works fine with Bun + Vite as long as this only runs in the browser.
let libHeifPromise: Promise<LibHeif> | null = null;

async function loadLibHeif(): Promise<LibHeif> {
  if (!libHeifPromise) {
    // If you see issues, you can also try importing the wasm bundle explicitly:
    // const m = await import("libheif-js/libheif-wasm/libheif-bundle.mjs");
    // const libheif = await (m.default || m)(); // init module
    // @ts-ignore
    const m = await import('libheif-js');
    const libheif = (m.default || m) as any;
    libHeifPromise = Promise.resolve(libheif as LibHeif);
  }
  return libHeifPromise;
}

export type HeicConvertOptions = {
  format?: 'image/png' | 'image/jpeg';
  quality?: number; // 0–1, JPEG only
};

export async function convertHeicFileToBlob(
  file: File,
  options: HeicConvertOptions = {},
): Promise<Blob> {
  const { format = 'image/jpeg', quality = 0.92 } = options;

  const arrayBuffer = await file.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const libheif = await loadLibHeif();
  const decoder = new libheif.HeifDecoder();

  const images = decoder.decode(uint8Array);
  if (!images.length) {
    throw new Error('No images found in HEIC file.');
  }

  const image = images[0] as HeifImage;

  const width = image.get_width();
  const height = image.get_height();

  // Prepare canvas + ImageData
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');

  const imageData = ctx.createImageData(width, height);

  // This is the crucial bit: you *must* call image.display
  // otherwise you just see that weird internal object with null img.
  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (displayData) => {
      if (!displayData) {
        reject(new Error('HEIF processing error (displayData is null)'));
        return;
      }

      // displayData.data is the RGBA buffer
      imageData.data.set(displayData.data);
      resolve();
    });
  });

  ctx.putImageData(imageData, 0, 0);

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) return reject(new Error('Failed to create Blob from canvas.'));
        resolve(b);
      },
      format,
      format === 'image/jpeg' ? quality : undefined,
    );
  });

  return blob;
}
